/**
 * "Teslyn Mobile" in the staff menu (docs/specs/mobile/mobile-distribution.md §3; decision 519: Android from Teslyn
 * itself, no Play listing; decision 912: only signed-in staff download it). The page shows the channel's latest
 * version, "Download Teslyn Mobile", the QR code and the install steps.
 *
 * The APK is in a private Cloudflare R2 bucket: the `mobile-download` Edge Function, called with the person's
 * sign-in, answers the release and a 15-minute link to it (asked again when Download is pressed, so the link is
 * always fresh). The QR code leads to `/mobile` on this address, which opens this page after the sign-in on the
 * phone's browser (boot.tsx). The staging portal hands out the beta channel, production the stable (decision 698).
 * They are two Android apps (GL-17): staging's page offers "Teslyn Mobile (beta)" (`au.teslyn.mobile.beta`),
 * production's "Teslyn Mobile" (`au.teslyn.mobile`); both can be on one phone.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  mobileAppId,
  mobileAppName,
  mobileChannelFor,
  parseMobileRelease,
  type MobileChannel,
  type MobileRelease,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { PageHeader } from '../components/States.tsx';
import { QrCode } from './QrCode.tsx';

/** The address the QR code carries: it opens this page after sign-in. */
export const MOBILE_PAGE_PATH = '/mobile';

export const isMobilePage = (pathname: string): boolean => /^\/mobile\/?$/.test(pathname);

/** `/mobile`'s search as the portal's: this page, keeping `?tenant=` on preview hosts. */
export function mobilePageSearch(search: string): string {
  const q = new URLSearchParams(search);
  const out = new URLSearchParams();
  const tenant = q.get('tenant');
  if (tenant) out.set('tenant', tenant);
  out.set('page', 'mobile-app');
  return `?${out.toString()}`;
}

/** The channel this build hands out. */
export function mobileChannel(env: ImportMetaEnv = import.meta.env): MobileChannel {
  return mobileChannelFor(env.VITE_TESLYN_ENVIRONMENT ?? 'staging');
}

export type MobileLink =
  | { readonly kind: 'ok'; readonly release: MobileRelease; readonly apkUrl: string }
  | { readonly kind: 'refused'; readonly words: string };

/** `mobile-download` as the signed-in person. */
export async function fetchMobileLink(
  sb: Pick<SupabaseClient, 'functions'>,
  channel: MobileChannel,
): Promise<MobileLink> {
  const { data, error } = await sb.functions.invoke<{ release?: unknown; apk_url?: unknown }>(
    'mobile-download',
    { body: { channel, app: mobileAppId(channel) } },
  );
  if (error) {
    const context = (error as { context?: { json?: () => Promise<unknown> } }).context;
    if (context && typeof context.json === 'function') {
      const body = (await context.json().catch(() => null)) as { error?: unknown } | null;
      if (typeof body?.error === 'string') return { kind: 'refused', words: body.error };
    }
    return { kind: 'refused', words: 'Teslyn Cloud could not be reached just now. Try again.' };
  }
  const parsed = parseMobileRelease(data?.release, channel);
  if ('problem' in parsed || typeof data?.apk_url !== 'string')
    return { kind: 'refused', words: 'The release cannot be used just now. Ask Braynewave.' };
  return { kind: 'ok', release: parsed.release, apkUrl: data.apk_url };
}

const DATE = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

export interface GetMobileProps {
  readonly sb: Pick<SupabaseClient, 'functions'>;
  readonly channel?: MobileChannel;
  /** The QR code's address (`<origin>/mobile`). */
  readonly pageUrl?: string;
  /** Opens the download (the browser follows the link). */
  readonly open?: (url: string) => void;
}

export function GetMobileScreen({
  sb,
  channel = mobileChannel(),
  pageUrl = typeof window === 'undefined' ? '' : window.location.origin + MOBILE_PAGE_PATH,
  open = (url) => window.location.assign(url),
}: GetMobileProps): ReactElement {
  const [link, setLink] = useState<MobileLink | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    void fetchMobileLink(sb, channel).then((l) => {
      if (live) setLink(l);
    });
    return () => {
      live = false;
    };
  }, [sb, channel]);

  // a fresh link each time: links last 15 minutes, and the page may have been open longer
  const download = useCallback(async () => {
    setBusy(true);
    const l = await fetchMobileLink(sb, channel);
    setLink(l);
    setBusy(false);
    if (l.kind === 'ok') open(l.apkUrl);
  }, [sb, channel, open]);

  const release = link?.kind === 'ok' ? link.release : null;
  return (
    <section className="page">
      <PageHeader title="Teslyn Mobile" />
      <div className="panel get-mobile">
        <p className="body">
          Teslyn Mobile is the crew&apos;s phone app: flights at the airport (with no signal if need be), the
          aircraft map, base station diurnal and alerts. It is for your organisation&apos;s staff and needs
          your Teslyn sign-in.
        </p>
        {link === null && <p className="body muted">Looking for the latest version…</p>}
        {link?.kind === 'refused' && (
          <p className="body" role="alert">
            {link.words}
          </p>
        )}
        {release && (
          <p className="body" role="status">
            Android, version <span className="data">{release.version}</span> ·{' '}
            <span className="data">{(release.size / 1048576).toFixed(1)}</span> MB
            {release.pub_date ? ` · ${DATE.format(new Date(release.pub_date))}` : ''}
            {channel === 'beta' ? ' · beta channel' : ''}
          </p>
        )}
        {release && (
          <div className="get-mobile__actions">
            <Button variant="primary" onClick={() => void download()} disabled={busy}>
              {busy ? 'Preparing the download…' : `Download ${mobileAppName(channel)}`}
            </Button>
          </div>
        )}
        <figure className="get-mobile__qr">
          <QrCode text={pageUrl} label="QR code: open the Teslyn Mobile page on your phone" />
          <figcaption className="caption muted">
            At a computer? Scan this with your Android phone&apos;s camera. The phone opens Teslyn Cloud: sign
            in with your Teslyn account and this page opens with Download.{' '}
            <span className="data">{pageUrl}</span>
          </figcaption>
        </figure>
        <h2 className="heading">Install it on Android</h2>
        <ol className="body" aria-label="How to install">
          <li>
            On the phone, scan the code (or open this page), sign in, and press Download{' '}
            {mobileAppName(channel)}.
          </li>
          <li>
            If Chrome warns that the file might be harmful, press Download anyway: it is Teslyn&apos;s own
            app, signed by Braynewave.
          </li>
          <li>Open the downloaded file (from the notice, or Files, Downloads).</li>
          <li>
            Android asks to allow your browser to install unknown apps: press Settings, turn on &quot;Allow
            from this source&quot;, then go back.
          </li>
          <li>Press Install, then Open, and sign in with your Teslyn account.</li>
        </ol>
        {channel === 'beta' && (
          <p className="body" data-testid="beta-own-app">
            Teslyn Mobile (beta) is its own app, for testing with Teslyn Cloud staging: it installs beside
            Teslyn Mobile, so a phone can have both. A phone that had the beta before 2026.10.17 has it as
            &quot;Teslyn Mobile&quot;: once Teslyn Mobile (beta) is installed and signed in, send that old
            app&apos;s waiting flights (Flights, Send now) and uninstall it.
          </p>
        )}
        <h2 className="heading">Updates</h2>
        <p className="body">
          Teslyn Mobile tells you when a new version is ready: press Install update. The first time, Android
          asks you to allow Teslyn Mobile to install apps, the same way.
        </p>
        <p className="caption muted">
          iPhone: the iPhone app comes through Apple&apos;s TestFlight; Braynewave sends you the invitation
          when it is ready.
        </p>
      </div>
    </section>
  );
}
